import { LegacyAuthResponse, LegacyJwtClaims } from '../types/legacy';

const TOKEN_KEY = 'glooci_legacy_token';
const LEGACY_COMPAT_TOKEN_KEY = 'legacy_token';
const AUTH_STATE_KEY = 'glooci_auth_state';
// DNS-switch safe mode: auth always goes through Firebase Hosting rewrite (/api).
const AUTH_API_BASES = ['/api'];

interface StoredAuthState {
  user: LegacyJwtClaims;
  expiresAtMs: number;
}

function decodeBase64Url(value: string): string {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padding = normalized.length % 4;
  const padded = padding ? `${normalized}${'='.repeat(4 - padding)}` : normalized;
  return atob(padded);
}

export function parseLegacyJwt(tokenWithBearer: string): LegacyJwtClaims | null {
  try {
    const rawToken = tokenWithBearer.replace(/^Bearer\s+/i, '');
    const [, payload] = rawToken.split('.');
    if (!payload) return null;
    const json = decodeBase64Url(payload);
    return JSON.parse(json) as LegacyJwtClaims;
  } catch {
    return null;
  }
}

export function getStoredLegacyToken(): string | null {
  return (
    localStorage.getItem(TOKEN_KEY)
    || localStorage.getItem(LEGACY_COMPAT_TOKEN_KEY)
    || sessionStorage.getItem(LEGACY_COMPAT_TOKEN_KEY)
  );
}

function getStoredModernAuthState(): StoredAuthState | null {
  try {
    const raw = localStorage.getItem(AUTH_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredAuthState>;
    if (!parsed.user || typeof parsed.user !== 'object') return null;
    if (!Number.isFinite(parsed.expiresAtMs) || Number(parsed.expiresAtMs) <= Date.now()) {
      localStorage.removeItem(AUTH_STATE_KEY);
      return null;
    }
    return parsed as StoredAuthState;
  } catch {
    localStorage.removeItem(AUTH_STATE_KEY);
    return null;
  }
}

export function saveLegacyToken(
  tokenWithBearer: string,
  user?: LegacyJwtClaims,
  expiresInSeconds?: number,
): void {
  localStorage.setItem(TOKEN_KEY, tokenWithBearer);
  localStorage.setItem(LEGACY_COMPAT_TOKEN_KEY, tokenWithBearer);
  sessionStorage.setItem(LEGACY_COMPAT_TOKEN_KEY, tokenWithBearer);
  if (user) {
    const ttlSeconds = Number.isFinite(Number(expiresInSeconds))
      ? Math.max(60, Number(expiresInSeconds))
      : 24 * 60 * 60;
    localStorage.setItem(AUTH_STATE_KEY, JSON.stringify({
      user,
      expiresAtMs: Date.now() + ttlSeconds * 1000,
    } satisfies StoredAuthState));
  }
}

export function clearLegacyToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(LEGACY_COMPAT_TOKEN_KEY);
  localStorage.removeItem(AUTH_STATE_KEY);
  sessionStorage.removeItem(LEGACY_COMPAT_TOKEN_KEY);
}

export function getCurrentLegacyUser(): LegacyJwtClaims | null {
  const token = getStoredLegacyToken();
  if (!token) return null;
  const claims = parseLegacyJwt(token);
  if (!claims) {
    const rawToken = token.replace(/^Bearer\s+/i, '');
    if (!rawToken.startsWith('glooci_user_v1_')) {
      clearLegacyToken();
      return null;
    }
    const storedState = getStoredModernAuthState();
    if (!storedState) {
      clearLegacyToken();
      return null;
    }
    return storedState.user;
  }
  if (!claims?.exp) return claims;
  const now = Math.floor(Date.now() / 1000);
  if (claims.exp < now) {
    clearLegacyToken();
    return null;
  }
  return claims;
}

export async function loginLegacy(username: string, password: string): Promise<LegacyJwtClaims> {
  let lastError: Error | null = null;

  for (let index = 0; index < AUTH_API_BASES.length; index += 1) {
    const base = AUTH_API_BASES[index];
    try {
      const response = await fetch(`${base}/authenticate`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          password,
          rememberMe: true,
          request: 'platform',
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        const err = new Error(errorText || `Login failed (${response.status})`);
        const canRetry = index < AUTH_API_BASES.length - 1 && (response.status >= 500 || response.status === 404 || response.status === 408);
        if (canRetry) {
          lastError = err;
          continue;
        }
        throw err;
      }

      const payload = (await response.json()) as LegacyAuthResponse;
      if (!payload.access_token) {
        throw new Error('Risposta autenticazione non valida: access_token mancante');
      }

      const tokenWithBearer = `Bearer ${payload.access_token}`;
      const claims = parseLegacyJwt(tokenWithBearer) || payload.user || { username };
      saveLegacyToken(tokenWithBearer, claims, payload.expires_in);

      return claims;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      const canRetry = index < AUTH_API_BASES.length - 1;
      if (canRetry) {
        lastError = err;
        continue;
      }
      throw err;
    }
  }

  throw lastError || new Error('Login failed: nessun endpoint disponibile');
}
